/* GASSISA
========================================================================================
* Descripción: Menú de acciones para gestión de caja
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 23/03/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 24/03/2026 | Lic. José Luis Aragón Cervantes  | Agregar reimpresión
*      3       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Protección anti doble click en acciones críticas
* ---------------------------------------------------------------------------------------
*/

import { Fragment, useState, useEffect } from "react";
import { IconButton, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText, Divider, Badge, Chip, Box } from "@mui/material";
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import LoginIcon from '@mui/icons-material/Login';
import PrintIcon from '@mui/icons-material/Print';
import ContentCutIcon from '@mui/icons-material/ContentCut';

import { useSesion } from "../../context/System/SesionContext";
import { useLayout } from "../../context/System/LayoutContext";
import { useRequest } from "../../context/System/RequestContext";
import { useVentas } from "../../context/Ventas/VentasContext";
import { apiStatusCaja } from "../../settings/ApiConfig";
import { useDoubleClickPrevention } from "../../hooks/useDoubleClickPrevention";

export default function MnuGestionCajaActions(props) {
    const {
        onAbrirAperturaCaja,
        onAbrirCierreCaja,
        onAbrirRetiroCapital,
        onAbrirReimpresion,
        statusCaja,
        cajaSeleccionada,
        statusHerramientasAdministrativas = false,
        listaCortes = [],
        onGenerarReporteCorteDetallado
    } = props;

    const { parametrosCajaForm, usuarioData } = useSesion();
    const { handleOpenAlert } = useLayout();
    const { GetRequest } = useRequest();
    const { handleImprimirTicketCorte } = useVentas();
    const { isProcessing, executeWithProtection } = useDoubleClickPrevention();

    const [anchorEl, setAnchorEl] = useState(null);
    const [cajaAbierta, setCajaAbierta] = useState(false);
    const [nombreCaja, setNombreCaja] = useState("");
    const open = Boolean(anchorEl);

    const getCajaValue = (caja) => {
        if (!caja) return null;
        const pc = caja.pc ?? caja.PC ?? caja.value ?? caja.label ?? null;
        if (pc === null) {
            const strCaja = String(caja);
            return strCaja && strCaja !== "" && strCaja !== "[object Object]" ? strCaja : null;
        }
        return String(pc).trim() || null;
    };

    const getNombreCaja = () => {
        return getCajaValue(cajaSeleccionada) || "";
    };

    const getCajaParaApi = () => {
        return getCajaValue(cajaSeleccionada);
    };

    useEffect(() => {
        if (statusCaja !== null && statusCaja !== undefined) {
            setCajaAbierta(statusCaja?.abierta ?? false);
        }
    }, [statusCaja]);

    useEffect(() => {
        const nombre = getNombreCaja();
        setNombreCaja(nombre);

        const fetchStatus = async () => {
            const caja = getCajaParaApi();
            if (!caja || caja === 'CAJA-DEFAULT') {
                setCajaAbierta(false);
                return;
            }

            try {
                const url = apiStatusCaja.replace("{caja}", caja);
                const response = await GetRequest({ url });

                if (response.hasError) {
                    setCajaAbierta(false);
                    return;
                }

                setCajaAbierta(response.result?.abierta ?? false);
            } catch (error) {
                setCajaAbierta(false);
            }
        };
        fetchStatus();
    }, [cajaSeleccionada]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const verificarStatusCaja = async () => {
        const caja = getCajaParaApi();
        if (!caja || caja === 'CAJA-DEFAULT') {
            return null;
        }

        try {
            const url = apiStatusCaja.replace("{caja}", caja);
            const response = await GetRequest({ url });

            if (response.hasError) {
                return null;
            }

            return response.result;
        } catch (error) {
            return null;
        }
    };

    const handleAperturaCaja = async () => {
        const caja = getCajaParaApi();
        if (!caja) {
            handleOpenAlert("Seleccione una caja del listado", "warning");
            return;
        }
        handleClose();
        const status = await verificarStatusCaja();

        if (status?.abierta) {
            handleOpenAlert("La caja ya está abierta", "info");
            return;
        }

        if (onAbrirAperturaCaja) {
            onAbrirAperturaCaja(status);
        }
    };

    const handleCierreCaja = async () => {
        const caja = getCajaParaApi();
        if (!caja) {
            handleOpenAlert("Seleccione una caja del listado", "warning");
            return;
        }
        handleClose();
        const status = await verificarStatusCaja();

        if (!status?.abierta) {
            handleOpenAlert("La caja ya está cerrada", "info");
            return;
        }

        if (onAbrirCierreCaja) {
            onAbrirCierreCaja(status);
        }
    };

    const handleRetiroCapital = async () => {
        const caja = getCajaParaApi();
        if (!caja) {
            handleOpenAlert("Seleccione una caja del listado", "warning");
            return;
        }
        handleClose();
        const status = await verificarStatusCaja();

        if (!status?.abierta) {
            handleOpenAlert("La caja está cerrada. Abra la caja primero.", "warning");
            return;
        }

        if (onAbrirRetiroCapital) {
            onAbrirRetiroCapital(status);
        }
    };

    const handleReimpresionApertura = () => {
        const caja = getCajaParaApi();
        if (!caja) {
            handleOpenAlert("Seleccione una caja del listado", "warning");
            return;
        }
        handleClose();
        if (onAbrirReimpresion) {
            onAbrirReimpresion('apertura');
        }
    };

    const handleReimpresionCierre = () => {
        const caja = getCajaParaApi();
        if (!caja) {
            handleOpenAlert("Seleccione una caja del listado", "warning");
            return;
        }
        handleClose();
        if (onAbrirReimpresion) {
            onAbrirReimpresion('cierre');
        }
    };

    const handleReimpresionRetiro = () => {
        const caja = getCajaParaApi();
        if (!caja) {
            handleOpenAlert("Seleccione una caja del listado", "warning");
            return;
        }
        handleClose();
        if (onAbrirReimpresion) {
            onAbrirReimpresion('retiro');
        }
    };

    const handleReimpresionCorte = () => {
        handleClose();
        if (listaCortes && listaCortes.length > 0) {
            handleImprimirTicketCorte(listaCortes);
        } else {
            handleOpenAlert("No hay datos de corte para reimprimir", "warning");
        }
    };

    const handleExportarCorteDetallado = () => {
        handleClose();
        if (onGenerarReporteCorteDetallado) {
            onGenerarReporteCorteDetallado();
        }
    };



    return (
        <Fragment>
            <Tooltip title="Gestión de Caja">
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                    <IconButton
                        onClick={handleClick}
                        size="small"
                        aria-controls={open ? 'gestion-caja-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                    >
                        <Badge
                            variant="dot"
                            color={cajaAbierta ? "success" : "error"}
                            invisible={!nombreCaja || nombreCaja === 'CAJA-DEFAULT'}
                        >
                            <AccountBalanceWalletIcon className="icon" />
                        </Badge>
                    </IconButton>
                    {nombreCaja && nombreCaja !== 'CAJA-DEFAULT' && (
                        <Chip
                            label={`${nombreCaja} ${cajaAbierta ? '✓' : '✗'}`}
                            size="small"
                            color={cajaAbierta ? "success" : "error"}
                            sx={{ ml: 0.5, height: 24, fontSize: '0.75rem' }}
                        />
                    )}
                </Box>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                id="gestion-caja-menu"
                open={open}
                onClose={handleClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        mt: 1.5,
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                        },
                    },
                }}
            >
                <MenuItem onClick={() => executeWithProtection(handleAperturaCaja)} disabled={isProcessing}>
                    <ListItemIcon>
                        <LockOpenIcon fontSize="small" color={cajaAbierta ? "disabled" : "success"} />
                    </ListItemIcon>
                    <ListItemText>
                        Apertura de Caja
                    </ListItemText>
                </MenuItem>

                <MenuItem onClick={() => executeWithProtection(handleCierreCaja)} disabled={isProcessing || !cajaAbierta}>
                    <ListItemIcon>
                        <LockIcon fontSize="small" color={cajaAbierta ? "error" : "disabled"} />
                    </ListItemIcon>
                    <ListItemText>
                        Cierre de Caja
                    </ListItemText>
                </MenuItem>

                <Divider />

                <MenuItem onClick={() => executeWithProtection(handleRetiroCapital)} disabled={isProcessing || !cajaAbierta}>
                    <ListItemIcon>
                        <RemoveCircleOutlineIcon fontSize="small" color={cajaAbierta ? "warning" : "disabled"} />
                    </ListItemIcon>
                    <ListItemText>
                        Retiro de Capital
                    </ListItemText>
                </MenuItem>

                <Divider />

                <MenuItem onClick={() => executeWithProtection(handleReimpresionApertura)} disabled={isProcessing || !statusHerramientasAdministrativas}>
                    <ListItemIcon>
                        <PrintIcon fontSize="small" color={statusHerramientasAdministrativas ? "info" : "disabled"} />
                    </ListItemIcon>
                    <ListItemText>
                        Reimprimir Apertura
                    </ListItemText>
                </MenuItem>

                <MenuItem onClick={() => executeWithProtection(handleReimpresionCierre)} disabled={isProcessing || !statusHerramientasAdministrativas}>
                    <ListItemIcon>
                        <PrintIcon fontSize="small" color={statusHerramientasAdministrativas ? "info" : "disabled"} />
                    </ListItemIcon>
                    <ListItemText>
                        Reimprimir Cierre
                    </ListItemText>
                </MenuItem>

                <MenuItem onClick={() => executeWithProtection(handleReimpresionRetiro)} disabled={isProcessing || !statusHerramientasAdministrativas}>
                    <ListItemIcon>
                        <PrintIcon fontSize="small" color={statusHerramientasAdministrativas ? "info" : "disabled"} />
                    </ListItemIcon>
                    <ListItemText>
                        Reimprimir Retiro
                    </ListItemText>
                </MenuItem>

                <MenuItem onClick={() => executeWithProtection(handleReimpresionCorte)} disabled={isProcessing || !statusHerramientasAdministrativas}>
                    <ListItemIcon>
                        <PrintIcon fontSize="small" color={statusHerramientasAdministrativas ? "info" : "disabled"} />
                    </ListItemIcon>
                    <ListItemText>
                        Reimprimir Corte
                    </ListItemText>
                </MenuItem>

                <MenuItem onClick={() => executeWithProtection(handleExportarCorteDetallado)} disabled={isProcessing || !statusHerramientasAdministrativas}>
                    <ListItemIcon>
                        <ContentCutIcon fontSize="small" color={statusHerramientasAdministrativas ? "info" : "disabled"} />
                    </ListItemIcon>
                    <ListItemText>
                        Exportar Corte Detallado
                    </ListItemText>
                </MenuItem>

                <Divider />

                <MenuItem disabled>
                    <ListItemIcon>
                        <PointOfSaleIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText inset>
                        Caja: {nombreCaja || "No seleccionada"}
                    </ListItemText>
                </MenuItem>

                <MenuItem disabled>
                    <ListItemText inset>
                        Status: {cajaAbierta ? "ABIERTA" : "CERRADA"}
                    </ListItemText>
                </MenuItem>
            </Menu>
        </Fragment>
    );
}
